import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, X, Loader2, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ACCOMMODATION_OPTIONS = [
  { value: 'appartement-1', label: "L'Appartement Terrasse" },
  { value: 'appartement-2', label: "L'Appartement Panorama" },
  { value: 'gite-seul', label: 'Gite seul' },
  { value: 'gite-salle', label: 'Gite + Salle' },
];

// Auto-guess mapping based on Amenitiz room labels
function guessMapping(label) {
  const l = (label || '').toLowerCase();
  if (l.includes('8 voyageurs')) return 'appartement-1';
  if (l.includes('12 voyageurs')) return 'appartement-2';
  if (l.includes('événement') || l.includes('evenement') || l.includes('domaine')) return 'gite-salle';
  if (l.includes('appartement') && l.includes('1')) return 'appartement-1';
  if (l.includes('appartement') && l.includes('2')) return 'appartement-2';
  if (l.includes('gite')) return 'gite-seul';
  return 'gite-seul';
}

export default function AdminImportReservations() {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [blockFuture, setBlockFuture] = useState(true);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const token = localStorage.getItem('admin_token');

  const handleFile = async (f) => {
    if (!f) return;
    const ok = /\.(xlsx|xls|csv)$/i.test(f.name);
    if (!ok) {
      setError('Format non supporte. Choisissez un fichier .xlsx ou .csv');
      return;
    }
    setError('');
    setReport(null);
    setFile(f);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await axios.post(`${BACKEND_URL}/api/admin/import-reservations/preview`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPreview(res.data);
      const initial = {};
      (res.data.room_names || []).forEach(name => { initial[name] = guessMapping(name); });
      setMapping(initial);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur lors de l\u2019analyse du fichier');
      setPreview(null);
      setFile(null);
    } finally {
      setLoading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
  };

  const runImport = async () => {
    if (!file) return;
    setLoading(true);
    setError('');
    setReport(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('accommodation_mapping', JSON.stringify(mapping));
      fd.append('block_future_dates', blockFuture ? 'true' : 'false');
      const res = await axios.post(`${BACKEND_URL}/api/admin/import-reservations`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReport(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur lors de l\u2019import');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setMapping({});
    setReport(null);
    setError('');
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="space-y-6" data-testid="admin-import-reservations">
      <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-xl font-light text-[#1A1C19]">Importer depuis Amenitiz</h2>
          {file && (
            <Button variant="outline" size="sm" onClick={reset} data-testid="import-reset-btn">
              <X className="w-4 h-4 mr-1" /> Nouveau fichier
            </Button>
          )}
        </div>
        <p className="text-sm text-[#595959] mb-6">
          Deposez votre export Amenitiz (.xlsx ou .csv). Deduplique automatiquement par <strong>ID Amenitiz</strong> et bloque les dates futures pour les reservations confirmees.
        </p>

        {!file && (
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition ${
              dragOver ? 'border-[#C4A962] bg-[#FAF7F0]' : 'border-[#E5E5E0] hover:border-[#C4A962] hover:bg-[#FAFAF8]'
            }`}
            data-testid="import-dropzone"
          >
            <Upload className="w-10 h-10 mx-auto text-[#C4A962] mb-3" strokeWidth={1.5} />
            <p className="text-[#2C3E30] font-medium">Glissez votre fichier ici</p>
            <p className="text-xs text-[#8A8A85] mt-1">ou cliquez pour parcourir &middot; .xlsx, .csv</p>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="import-file-input"
            />
          </div>
        )}

        {file && (
          <div className="flex items-center gap-3 p-3 rounded-xl bg-[#FAFAF8] border border-[#E5E5E0]">
            <FileSpreadsheet className="w-5 h-5 text-[#C4A962]" strokeWidth={1.5} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#1A1C19] truncate">{file.name}</p>
              <p className="text-xs text-[#8A8A85]">{(file.size / 1024).toFixed(1)} KB</p>
            </div>
            {preview && (
              <div className="text-right">
                <p className="text-sm font-medium text-[#2C3E30]">{preview.total_rows} lignes</p>
                {preview.header_line > 1 && (
                  <p className="text-xs text-[#8A8A85]">En-tete detecte ligne {preview.header_line}</p>
                )}
                {preview.already_imported > 0 && (
                  <p className="text-xs text-sky-700">{preview.already_imported} deja presentes : leurs champs financiers seront mis a jour</p>
                )}
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="mt-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2" data-testid="import-error">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>

      {preview && !report && (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6" data-testid="import-mapping">
          <h3 className="text-lg font-light text-[#1A1C19] mb-1">Mapper les logements</h3>
          <p className="text-sm text-[#595959] mb-5">
            Pour chaque nom de logement Amenitiz, choisissez le type correspondant dans notre systeme.
          </p>
          <div className="space-y-3">
            {(preview.room_names || []).map(name => (
              <div key={name} className="flex items-center gap-3 p-3 rounded-xl bg-[#FAFAF8] border border-[#E5E5E0]">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#1A1C19] line-clamp-2" title={name}>{name}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-[#8A8A85] flex-shrink-0" />
                <Select value={mapping[name] || 'gite-seul'} onValueChange={(v) => setMapping(m => ({ ...m, [name]: v }))}>
                  <SelectTrigger className="w-[210px]" data-testid={`mapping-select-${name.slice(0, 20)}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACCOMMODATION_OPTIONS.map(o => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
            {preview.room_names?.length === 0 && (
              <p className="text-sm text-[#8A8A85] italic">Aucun nom de logement detecte dans le fichier.</p>
            )}
          </div>

          <label className="flex items-center gap-2 mt-5 cursor-pointer">
            <input
              type="checkbox"
              checked={blockFuture}
              onChange={(e) => setBlockFuture(e.target.checked)}
              className="w-4 h-4 accent-[#2C3E30]"
              data-testid="import-block-future-checkbox"
            />
            <span className="text-sm text-[#595959]">
              Bloquer le calendrier pour les reservations confirmees avec check-out futur
            </span>
          </label>

          <div className="flex justify-end mt-5">
            <Button
              onClick={runImport}
              disabled={loading}
              className="bg-[#2C3E30] hover:bg-[#3a5040] text-white rounded-full px-6"
              data-testid="import-run-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Lancer l'import
            </Button>
          </div>
        </div>
      )}

      {report && (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6" data-testid="import-report">
          <div className="flex items-center gap-2 mb-5">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
            <h3 className="text-lg font-light text-[#1A1C19]">Rapport d'import</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
            <div className="p-4 rounded-xl bg-[#FAFAF8] border border-[#E5E5E0]">
              <p className="text-xs uppercase tracking-wider text-[#8A8A85] mb-1">Total</p>
              <p className="text-2xl font-light text-[#1A1C19]" data-testid="report-total">{report.total_rows}</p>
            </div>
            <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200">
              <p className="text-xs uppercase tracking-wider text-emerald-700 mb-1">Importees</p>
              <p className="text-2xl font-light text-emerald-900" data-testid="report-imported">{report.imported}</p>
            </div>
            <div className="p-4 rounded-xl bg-sky-50 border border-sky-200">
              <p className="text-xs uppercase tracking-wider text-sky-700 mb-1">Mises a jour</p>
              <p className="text-2xl font-light text-sky-900" data-testid="report-updated">{report.updated || 0}</p>
            </div>
            <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
              <p className="text-xs uppercase tracking-wider text-amber-700 mb-1">Ignorees</p>
              <p className="text-2xl font-light text-amber-900" data-testid="report-skipped">{report.skipped}</p>
            </div>
            <div className="p-4 rounded-xl bg-red-50 border border-red-200">
              <p className="text-xs uppercase tracking-wider text-red-700 mb-1">Erreurs</p>
              <p className="text-2xl font-light text-red-900" data-testid="report-errors">{report.errors?.length || 0}</p>
            </div>
          </div>

          {report.blocked_future > 0 && (
            <p className="text-sm text-[#595959] mb-4">
              <strong>{report.blocked_future}</strong> reservation(s) confirmee(s) ont bloque automatiquement leurs dates futures dans le calendrier.
            </p>
          )}

          {report.errors?.length > 0 && (
            <div className="border-t border-[#E5E5E0] pt-4">
              <p className="text-sm font-medium text-[#1A1C19] mb-2">Detail des erreurs</p>
              <div className="max-h-64 overflow-y-auto space-y-1">
                {report.errors.slice(0, 50).map((e, i) => (
                  <div key={i} className="text-xs p-2 rounded bg-red-50 text-red-800 flex gap-3">
                    <span className="font-mono">Ligne {e.row}</span>
                    {e.amenitiz_id && <span className="font-mono text-red-600">#{e.amenitiz_id}</span>}
                    <span className="flex-1">{e.reason}</span>
                  </div>
                ))}
                {report.errors.length > 50 && (
                  <p className="text-xs text-[#8A8A85] italic">+ {report.errors.length - 50} autres erreurs</p>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end mt-5">
            <Button variant="outline" onClick={reset} data-testid="import-finish-btn">
              Importer un autre fichier
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
